import { useState, useEffect, useMemo, useCallback } from "react";
import TableCertificado from "../../Components/TableCertificados";
import { formatDateTime } from "../../Components/jwtUtils";
import Api from "../../Auth/Api";
import { pdf } from "@react-pdf/renderer";
import GerarCertificado from "../../Components/GerarCertificado";
import { useAjustes } from "../../Context/AjustesContext";
import { Toast } from "react-bootstrap";

function Certificado() {

    const token = JSON.parse(localStorage.getItem("user_token"));
    const { ajustes } = useAjustes();
    const [toast, setToast] = useState({});
    const [showToast, setShowToast] = useState(false);

    //listar todos os cursos
    const [dataTodosCursos, setDataTodosCursos] = useState([]);
    useEffect(() => {
        async function fetchData() {
            try {
                const response = await Api.get(`/api/listar-todos-cursos`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                setDataTodosCursos(response.data);
            } catch (error) {
                console.error(error.response.data.message)
            }
        }
        fetchData();
    }, [token])

    // listar certificados
    const [dataCertificados, setDataCertificados] = useState([]);
    useEffect(() => {
        async function fetchData() {
            try {
                const response = await Api.get(`/api/listar-certificados-aluno`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                setDataCertificados(response.data);
            } catch (error) {
                setDataCertificados([]); // Se não tiver dados, limpa a lista
                console.error(error.response.data.message)
            }
        }
        fetchData();
    }, [token])

    // Filtro para seleção por curso
    const [selectCurso, setSelectCurso] = useState(null);
    const FilterDataProvas = selectCurso ? dataCertificados.filter(item => item.idCurso === selectCurso) : dataCertificados;

    // Estado para armazenar os status de expiração dos certificados
    const [certificadosStatus, setCertificadosStatus] = useState({});
    
    // Carregar status de expiração para todos os certificados listados
    useEffect(() => {
        async function carregarStatusExpiracao() {
            if (dataCertificados.length === 0) return;
            
            const status = {};
            for (const cert of dataCertificados) {
                try {
                    const response = await Api.get(`api/verificar-expiracao-certificado/${cert.id}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    status[cert.id] = response.data;
                } catch (error) {
                    console.error(`Erro ao verificar expiração do certificado ${cert.id}:`, error);
                }
            }
            setCertificadosStatus(status);
        }
        
        carregarStatusExpiracao();
    }, [dataCertificados, token]);

    // Função para verificar expiração e gerar o PDF
    const handleDownloadPDF = useCallback(async (certificado) => {
        try {
            // Verificar se o certificado está expirado
            const verificacaoExpiracao = await Api.get(`api/verificar-expiracao-certificado/${certificado.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            // Se o certificado está expirado, mostra mensagem e não permite download
            if (verificacaoExpiracao.data.expirado) {
                setToast({
                    header: 'Certificado expirado!',
                    body: "Este certificado expirou. É necessário refazer o curso para obter um novo certificado."
                });
                setShowToast(true);
                return;
            }
            
            // Obter informações da unidade do usuário
            const userInfo = await Api.get(`api/usuario/${certificado.idUsuario}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            // Dados para gerar o PDF, usando dados da verificação de expiração e do usuário
            const certificadoData = { 
                id: certificado.id, 
                nomeAluno: certificado.nomeAluno, 
                codigo: certificado.codigo,
                nomeCurso: certificado.nomeCurso, 
                prazo_validade: verificacaoExpiracao.data.prazo_validade, 
                data: certificado.data, 
                nomeGestor: certificado.nomeGestor, 
                certificado: certificado.certificado, 
                certificado_frente: certificado.certificado_frente,
                nomeTopico: certificado.nomeTopico, 
                cargaHorariaTotal: certificado.cargaHorariaTotal, 
                unidade: userInfo.data?.unidade || "Não especificada",
                empresa_nome: certificado.empresa_nome
            };
            
            // Gerar o PDF
            const pdfBlob = await pdf(
                <GerarCertificado 
                    data={certificadoData} 
                    diplomaUrl={ajustes.diploma}
                    empresa={certificadoData.empresa_nome || ajustes.titulo}
                />
            ).toBlob();
            
            // Criar URL e fazer download
            const url = URL.createObjectURL(pdfBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `certificado_${certificado.nomeAluno}_${certificado.nomeCurso}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
        } catch (error) {
            console.error("Erro ao gerar o PDF:", error);
            setToast({ 
                header: 'Erro!', 
                body: "Ocorreu um erro ao gerar o certificado. Tente novamente." 
            });
            setShowToast(true);
        }
    }, [token, ajustes.diploma, ajustes.titulo]);

    const columns = useMemo(
        () => [
            {
                Header: () => null, // Sem título para esta coluna
                id: 'icon',
                accessor: row => row.id,
                Cell: () => (
                    <div className="d-flex align-items-center justify-content-center">
                        <i className="bi bi-card-checklist fs-3"></i>
                    </div>
                )
            },
            {
                Header: 'Curso',
                accessor: 'nomeCurso'
            },
            {
                Header: 'Gestor',
                accessor: 'nomeGestor',
                Cell: ({ value }) => (
                    value ? value : "Automático"
                )
            },
            {
                Header: () => null, // Sem título para esta coluna
                id: 'data',
                accessor: row => row.data,
                Cell: ({ cell: { value }, row: { original } }) => (
                    <div className="small text-center">{formatDateTime(value)}</div>
                )
            },
            {
                Header: () => 'Status',
                id: 'status',
                accessor: row => row.id,
                Cell: ({ cell: { value }, row: { original } }) => {
                    const status = certificadosStatus[value];
                    if (!status) return <div className="text-center"><span className="spinner-border spinner-border-sm" role="status"></span></div>;
                    
                    if (status.prazo_validade === null || status.prazo_validade === 0) {
                        return (
                            <div className="text-center">
                                <span className="badge bg-info">Permanente</span>
                            </div>
                        );
                    }
                    
                    if (status.arquivado || status.expirado) {
                        return (
                            <div className="text-center">
                                <span className="badge bg-danger">Expirado</span>
                               
                            </div>
                        );
                    }
                    
                    return (
                        <div className="text-center">
                            <span className="badge bg-success">Válido</span>
                            <div className="small text-muted">até {new Date(status.data_expiracao).toLocaleDateString()}</div>
                        </div>
                    );
                }
            },
            {
                Header: () => null, // Sem título para esta coluna
                id: 'id',
                accessor: row => row.id,
                Cell: ({ cell: { value }, row: { original } }) => {
                    return (
                        <div className="d-flex align-items-center justify-content-center">
                            <button 
                                onClick={() => handleDownloadPDF(original)} 
                                className="btn btn-outline-primary rounded px-2"
                                disabled={certificadosStatus[value]?.expirado}
                            >
                                <i className="bi bi-download" />
                            </button>
                        </div>
                    );
                }
            }
        ],
        [handleDownloadPDF, certificadosStatus]
    )

    return (
        <>
            <div className="fade-in w-100">
                <span className="text-primary fs-1">Certificados</span>

                <p className="fs-6">
                    Lista com todos os seus certificados disponíveis
                </p>

                <TableCertificado columns={columns} data={FilterDataProvas} filterSelect={dataTodosCursos} search={false} setSelectCurso={setSelectCurso} titulo="Lista de Certificados" />
            </div>
            
            {/* Toast para mostrar mensagens */}
            <Toast show={showToast} onClose={() => setShowToast(false)} delay={3000} autohide style={{ position: 'fixed', left: '95%', bottom: '10px', transform: 'translateX(-95%)', zIndex: 9999, padding: 0 }}>
                <Toast.Header>
                    <strong className="mr-auto">{toast.header}</strong>
                </Toast.Header>
                <Toast.Body>{toast.body}</Toast.Body>
            </Toast>
        </>
    )
}

export default Certificado;
